import { useEffect, useState } from 'react';
import { Download, RotateCw, TriangleAlert } from 'lucide-react';
import type { RuntimeComponentDownloadStatus } from '../shared/desktop-api';
import { floorRuntimeDownload } from './runtime-download-floor';

/** The words the status bar uses while the background components arrive. */
export const RUNTIME_DOWNLOAD_NOTE = 'Accuracy will improve once the download completes';

const localStore = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** Live background-component status; null until main answers (or in a browser preview). */
export function useRuntimeComponentStatus(): RuntimeComponentDownloadStatus | null {
  const [status, setStatus] = useState<RuntimeComponentDownloadStatus | null>(null);
  useEffect(() => {
    let live = true,
      pushed = false;
    const api = window.capturePointDesktop;
    const shown = (next: RuntimeComponentDownloadStatus) =>
      floorRuntimeDownload(next, localStore());
    const stop = api?.onRuntimeComponentStatusChanged?.((next) => {
      pushed = true;
      if (live) setStatus(shown(next));
    });
    void api
      ?.getRuntimeComponentStatus?.()
      .then((next) => {
        if (live && !pushed) setStatus(shown(next));
      })
      .catch(() => undefined);
    return () => {
      live = false;
      stop?.();
    };
  }, []);
  return status;
}

/** Visible until the background download finishes, on every computer. */
export const runtimeDownloadVisible = (status: RuntimeComponentDownloadStatus | null) =>
  status?.state === 'downloading' || status?.state === 'failed';

/**
 * The status-bar item: the note and a percentage while downloading; the failure
 * and Retry in the same place when a component could not be fetched. Nothing
 * once the download has completed (or when there was nothing to fetch).
 */
export function RuntimeComponentDownload({
  status,
}: {
  status: RuntimeComponentDownloadStatus | null;
}) {
  const [retrying, setRetrying] = useState(false);
  if (!status || !runtimeDownloadVisible(status)) return null;
  const percent = Math.min(99, Math.floor(status.fraction * 100));
  if (status.state === 'failed') {
    return (
      <span className="runtime-download-status failed" role="status">
        <TriangleAlert size={13} aria-hidden="true" />
        <span>{status.message ?? 'The download stopped.'}</span>
        <button
          type="button"
          className="runtime-download-retry"
          disabled={retrying}
          onClick={() => {
            setRetrying(true);
            void window.capturePointDesktop
              ?.retryRuntimeComponents?.()
              .catch(() => undefined)
              .finally(() => setRetrying(false));
          }}
        >
          <RotateCw size={12} aria-hidden="true" />
          Retry
        </button>
      </span>
    );
  }
  return (
    <span
      className="runtime-download-status"
      role="status"
      aria-label={`${RUNTIME_DOWNLOAD_NOTE}. Downloading, ${percent}%`}
    >
      <Download size={13} aria-hidden="true" />
      <span className="runtime-download-note">{RUNTIME_DOWNLOAD_NOTE}</span>
      <span className="runtime-download-meter" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </span>
      <strong>{percent}%</strong>
    </span>
  );
}
